import { supabase } from '@/integrations/supabase/client';
import { buildBaySnapshotsForJobs } from '@/utils/catalogBayRules';
import { isOfferableCatalogJob } from '@/lib/catalog/newJobCatalogQuery';
import { recomputeTicketPredictedMinutes } from '@/utils/recomputeTicketPredictedMinutes';

/**
 * CORE-BP item 6 — accepted suggestion work joins the ticket the ordinary way.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS COMPOSES RATHER THAN REIMPLEMENTS
 * ═══════════════════════════════════════════════════════════════════════════
 * A suggestion the human accepts on an existing ticket must become work that is
 * indistinguishable from work added any other way. The tempting shortcut — one
 * `ticket_jobs` insert — produces a row that looks right and behaves wrongly:
 * with no bay snapshots the gating validators see no requirements and report
 * every bay compatible, so a lift-required alignment can land in a flat bay;
 * and with no recompute the ticket's `predicted_minutes` still describes work
 * that is no longer all of it, so the board schedules against a stale total.
 *
 * So this adds nothing of its own. It calls the two helpers the repository
 * already treats as authoritative:
 *
 *   · `buildBaySnapshotsForJobs` — the same catalogue + shop-override merge
 *     AddJobDialog uses, batched, already used by OptiPlace and Catalyst;
 *   · `recomputeTicketPredictedMinutes` — whose own contract says every
 *     mutation affecting job time MUST funnel through it, and which owns the
 *     cascade and rollover-tail propagation.
 *
 * Existing add paths are deliberately NOT refactored onto this. They work, and
 * rewriting ticket mutation was not this slice's job; this is the minimum
 * primitive needed so the suggestion path does not become the one caller that
 * quietly skips the invariants.
 */

export interface AddCatalogJobsResult {
  /** `jobs.id` for each job actually created, in request order. */
  addedJobIds: string[];
  /** Catalogue ids that could not be added, with why. Never thrown away. */
  failed: Array<{ catalogJobId: string; reason: string }>;
}

/**
 * Add catalogue jobs to an EXISTING ticket.
 *
 * Batched on purpose: a human accepting three suggestions is one decision, so
 * it produces one recompute and one cascade rather than three of each.
 */
export async function addCatalogJobsToTicket(
  ticketId: string,
  catalogJobIds: readonly string[],
  options?: { userId?: string | null },
): Promise<AddCatalogJobsResult> {
  const result: AddCatalogJobsResult = { addedJobIds: [], failed: [] };
  if (catalogJobIds.length === 0) return result;

  const { data: ticket, error: ticketErr } = await supabase
    .from('tickets')
    .select('id, shop_id')
    .eq('id', ticketId)
    .maybeSingle();

  if (ticketErr || !ticket?.shop_id) {
    for (const id of catalogJobIds) {
      result.failed.push({ catalogJobId: id, reason: ticketErr?.message ?? 'ticket not found' });
    }
    return result;
  }
  const shopId = ticket.shop_id as string;

  const { data: catalogRows, error: catalogErr } = await supabase
    .from('jobs_catalog')
    // By configured id (add-on accepted work): a LOOKUP, so a retired row is
    // found — and then refused below rather than silently added (W2).
    .select('id, label, category_key, sub_menu, minutes_default, skill_level, proficiency_category, retired_at')
    .in('id', catalogJobIds as string[]);

  if (catalogErr) {
    for (const id of catalogJobIds) {
      result.failed.push({ catalogJobId: id, reason: catalogErr.message });
    }
    return result;
  }

  const byId = new Map((catalogRows ?? []).map((r) => [r.id as string, r]));
  const resolvable = catalogJobIds.filter((id) => byId.has(id) && isOfferableCatalogJob(byId.get(id)));
  for (const id of catalogJobIds) {
    if (!byId.has(id)) result.failed.push({ catalogJobId: id, reason: 'catalog job not found' });
    else if (!isOfferableCatalogJob(byId.get(id))) result.failed.push({ catalogJobId: id, reason: 'retired' });
  }
  if (resolvable.length === 0) return result;

  // The same bay-rule merge every other add path uses, in one batched read.
  const snapshots = await buildBaySnapshotsForJobs(
    shopId,
    resolvable.map((id) => ({
      catalog_job_id: id,
      category: (byId.get(id) as { category_key?: string | null })?.category_key ?? null,
    })),
  );

  // Skill level is stored numerically on `jobs`; the catalogue stores a word.
  // Mirrors AddJobDialog's map so an accepted suggestion is skill-gated the
  // same way the same catalogue job would be if added by hand.
  const skillLevelMap: Record<string, number> = {
    basic: 2, intermediate: 4, advanced: 7, expert: 9,
  };

  for (let i = 0; i < resolvable.length; i += 1) {
    const catalogJobId = resolvable[i];
    const c = byId.get(catalogJobId) as Record<string, unknown>;
    const snapshot = snapshots[i];

    const { data: jobData, error: jobErr } = await supabase
      .from('jobs')
      .insert({
        shop_id: shopId,
        name: String(c.label ?? ''),
        category: String(c.category_key ?? ''),
        submenu: String(c.sub_menu ?? ''),
        standard_minutes: Number(c.minutes_default ?? 60),
        skill_level: skillLevelMap[String(c.skill_level ?? '').toLowerCase()] ?? 2,
        bay_requirement: 'Lift',
        // Carried so the ticket's work keeps its catalogue IDENTITY — the same
        // identity the already-present guard reads on the next evaluation.
        catalog_job_id: catalogJobId,
        proficiency_category: (c.proficiency_category as string | null) ?? null,
      })
      .select('id')
      .single();

    if (jobErr || !jobData?.id) {
      result.failed.push({ catalogJobId, reason: jobErr?.message ?? 'job insert failed' });
      continue;
    }

    const { error: linkErr } = await supabase.from('ticket_jobs').insert({
      ticket_id: ticketId,
      job_id: jobData.id,
      created_by_user_id: options?.userId ?? null,
      allowed_bay_types_snapshot: snapshot?.allowed_bay_types_snapshot ?? [],
      required_capabilities_snapshot: snapshot?.required_capabilities_snapshot ?? [],
      excluded_bay_types_snapshot: snapshot?.excluded_bay_types_snapshot ?? [],
      bay_requirement_source: snapshot?.bay_requirement_source ?? 'catalog',
    });

    if (linkErr) {
      result.failed.push({ catalogJobId, reason: linkErr.message });
      continue;
    }
    result.addedJobIds.push(jobData.id as string);
  }

  // Once, after the batch reaches its settled state — never per row. The
  // helper owns predicted_minutes, the board cascade and rollover-tail
  // propagation, so nothing here needs to know about any of them.
  if (result.addedJobIds.length > 0) {
    await recomputeTicketPredictedMinutes(ticketId, { userId: options?.userId ?? undefined });
  }

  return result;
}
